import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal } from "./Reveal";
import { SHARES, fmtSteers } from "@/lib/beef";
import { Check, Beef } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Pricing = () => {
  const [availability, setAvailability] = useState(null);
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    axios.get(`${API}/availability`).then(({ data }) => setAvailability(data)).catch(() => {});
    axios.get(`${API}/pricing`).then(({ data }) => setPrices(data)).catch(() => {});
  }, []);

  const priceFor = (id) => {
    const p = prices?.[id];
    return p ? `$${Number(p).toFixed(2)}` : SHARES.find((s) => s.id === id).pricePerLb;
  };

  const depositFor = (id) => {
    const d = prices?.[`deposit_${id}`];
    const v = d !== undefined ? Number(d) : SHARES.find((s) => s.id === id).deposit;
    return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  };

  const typicalFor = (id) => prices?.[`typical_${id}`] || SHARES.find((s) => s.id === id).typical;

  return (
  <section id="pricing" className="py-24 lg:py-32" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-clay mb-4">Pricing</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium leading-tight max-w-xl">
            One price per pound of hanging weight. That's it.
          </h2>
          {availability && (
            <p className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-forest text-cream text-sm font-semibold px-5 py-2.5" data-testid="pricing-availability-pill">
              <Beef className="w-4 h-4" strokeWidth={1.5} />
              {availability.remaining > 0
                ? `Only ${fmtSteers(availability.remaining)} ${availability.remaining === 1 ? "steer" : "steers"} left this season`
                : "Sold out — join the waitlist below"}
            </p>
          )}
        </div>
        <p className="text-moss text-base leading-relaxed max-w-sm">
          Less per pound than the grocery store — and you know exactly where your beef
          comes from. Every fee is covered in the price you see.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {SHARES.map((s, i) => {
          const featured = s.id === "whole";
          return (
            <Reveal key={s.id} delay={i * 0.12} className={featured ? "lg:-mt-6" : i === 1 ? "lg:mt-6" : ""}>
              <div
                data-testid={`pricing-card-${s.id}`}
                className={`rounded-2xl p-10 h-full flex flex-col border transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg ${
                  featured ? "bg-forest text-cream border-forest" : "bg-white border-linen"
                }`}
              >
                {featured && (
                  <span className="self-start text-xs tracking-[0.2em] uppercase font-bold bg-clay text-cream rounded-full px-4 py-1.5 mb-6">
                    Feeds a crowd
                  </span>
                )}
                <h3 className="font-display text-3xl tracking-tight font-medium">{s.name}</h3>
                <p className={`text-sm mt-1 ${featured ? "text-cream/70" : "text-moss"}`}>{s.tagline}</p>

                <div className="mt-8 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-medium" data-testid={`pricing-price-${s.id}`}>{priceFor(s.id)}</span>
                  <span className={`text-sm ${featured ? "text-cream/70" : "text-moss"}`}>/ lb hanging</span>
                </div>

                <ul className={`mt-8 space-y-3.5 text-base flex-1 ${featured ? "text-cream/90" : "text-forest/85"}`}>
                  <li className="flex gap-3"><Check className="w-4 h-4 mt-0.5 shrink-0 text-clay" strokeWidth={2} /> Priced on the steer's actual hanging weight</li>
                  <li className="flex gap-3"><Check className="w-4 h-4 mt-0.5 shrink-0 text-clay" strokeWidth={2} /> {s.custom ? "Fully custom cuts — tell us, we handle the butcher" : "Butcher's standard cut — a balanced mix"}</li>
                  <li className="flex gap-3"><Check className="w-4 h-4 mt-0.5 shrink-0 text-clay" strokeWidth={2} /> {typicalFor(s.id)}</li>
                  <li className="flex gap-3"><Check className="w-4 h-4 mt-0.5 shrink-0 text-clay" strokeWidth={2} /> {s.note}</li>
                </ul>

                <a
                  href="#reserve"
                  data-testid={`pricing-reserve-${s.id}`}
                  className={`mt-10 rounded-full text-center font-semibold px-6 py-3.5 text-sm transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                    featured ? "bg-clay text-cream hover:bg-claydark" : "bg-forest text-cream hover:bg-forest/90"
                  }`}
                >
                  Reserve with ${depositFor(s.id)} deposit
                </a>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.05}>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="quarter-chart-block">
          <div className="lg:col-span-5 relative">
            <div className="absolute -top-4 -left-4 w-full h-full rounded-2xl border-2 border-clay/30" />
            <img
              src="/images/quarter-chart-clean.png"
              alt="What's included in a quarter cow: steaks, roasts, ground beef, and more"
              className="relative rounded-2xl w-full object-cover shadow-lg"
              data-testid="quarter-chart-img"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-clay mb-4">What's Included</p>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
              This is what a quarter actually looks like.
            </h3>
            <p className="text-moss text-base leading-relaxed mt-6 max-w-xl">
              Ribeyes, T-bones, sirloins, tri-tip, roasts, cube steaks, short ribs, brisket,
              35+ lbs of ground beef, soup bones, and fat for tallow. Exact amounts vary with
              each animal — you're priced on the actual hanging weight, never a guess.
            </p>
            <p className="text-moss text-base leading-relaxed mt-4 max-w-xl">
              Half and whole orders scale up from here and go fully custom.
            </p>
            <p className="mt-6 inline-flex rounded-xl border border-clay/30 bg-clay/5 px-5 py-3.5 text-sm font-semibold text-forest" data-testid="organs-note">
              Organs (liver, heart, tongue, oxtail) upon request — first come, first served. One customer per animal.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div
          className="mt-12 rounded-2xl border border-clay/30 bg-clay/5 px-8 py-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8"
          data-testid="grass-finished-callout"
        >
          <p className="font-display italic text-2xl text-clay shrink-0">By special request</p>
          <p className="text-forest/85 text-base leading-relaxed">
            Prefer 100% grass fed? We also offer <span className="font-semibold">grass fed, grass finished beef</span> —
            no grain finish at all — at a more premium price. Just mention it when you reserve or when you call Stacy.
          </p>
        </div>
      </Reveal>
      </div>
    </section>
  );
};
